import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { useAccommodationSearchPageRedirectUrl } from '../components/AccommodationsPushStep/useAccommodationSearchPageRedirectUrl';
import { useTranslation } from '@shared/core';
import { TranslateFunction } from '@locales/generated';
import { useCallback } from 'react';
import { useThanksForRSVPTelemetry } from '../ThanksForRSVP.telemetry';
import { useRsvpFeatureValue } from '@shared/hooks/useRsvpFeatureFlags';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

type CopyTextStructure<t> = {
  heading: t;
  subHeading: t;
  ctaText: t;
};

type NearbyHotelsCopyText = CopyTextStructure<string>;
type CopyTranslation = CopyTextStructure<TranslateFunction>;

type NearbyHotelsTranslation = {
  withCityCopy: CopyTranslation;
  genericCopy: CopyTranslation;
};

export type NearbyHotelsData = {
  isResolved: boolean;
  shouldShowNearbyHotels: boolean;
  nearbyHotelsCopyText: NearbyHotelsCopyText;
  nearbyHotelsCTALink: string;
  onClickTelemetry: () => void;
};

const getCopyTextForLocation = (translation: NearbyHotelsTranslation, city: Maybe<string>): NearbyHotelsCopyText => {
  if (!city) {
    return getGenericCopyText(translation);
  }

  return {
    heading: translation.withCityCopy.heading(),
    subHeading: translation.withCityCopy.subHeading({ city }),
    ctaText: translation.withCityCopy.ctaText(),
  };
};

const getGenericCopyText = (translation: NearbyHotelsTranslation): NearbyHotelsCopyText => {
  return {
    heading: translation.genericCopy.heading(),
    subHeading: translation.genericCopy.subHeading(),
    ctaText: translation.genericCopy.ctaText(),
  };
};

export const useNearbyHotelsData = (eventHandle: string, eventId: string): NearbyHotelsData => {
  const { isResolved, hasVisibleTravelEventPage, hasVisibleAccommodationsEventPage, eventFinalizedDate, eventLocationPlaceId, eventLocationCityName } = useEventAccommodationsCheck(
    {
      eventHandle,
    },
  );

  const { t } = useTranslation('thanksForRSVP');
  const tNearbyHotels = t('congratulationsCard', 'nearbyHotels');

  const isEligibleForExperiment = !hasVisibleTravelEventPage && !hasVisibleAccommodationsEventPage;
  const isInTreatmentGroup = useRsvpFeatureValue('postRSVPEnableNearbyHotelsCard', { skip: !isEligibleForExperiment }).value === 'treatment';
  const { postRsvpViewNearbyHotelsClickedMessaging, postRsvpViewNearbyHotelsClickedTravel } = useThanksForRSVPTelemetry();

  const { url: nearbyHotelsCTALink, params } = useAccommodationSearchPageRedirectUrl({ eventFinalizedDate, eventLocationPlaceId, eventId: eventId, eventLocationCityName });

  const onClickTelemetry = useCallback(() => {
    postRsvpViewNearbyHotelsClickedMessaging({ destinationURL: nearbyHotelsCTALink, queryParams: params });
    postRsvpViewNearbyHotelsClickedTravel({ destinationURL: nearbyHotelsCTALink, queryParams: params });
  }, [nearbyHotelsCTALink, params, postRsvpViewNearbyHotelsClickedMessaging, postRsvpViewNearbyHotelsClickedTravel]);

  const hotelRoute = useHotelRoutePaths();

  const hotelUrl = hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { utm_medium: 'web', utm_source: 'confirmation_rsvp', utm_campaign: 'postRSVPEnableNearbyHotels' });

  return {
    isResolved,
    shouldShowNearbyHotels: isInTreatmentGroup,
    nearbyHotelsCopyText: getCopyTextForLocation(tNearbyHotels, eventLocationCityName),
    nearbyHotelsCTALink: hotelUrl,
    onClickTelemetry,
  };
};
